import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { SWAPS_API_ENDPOINTS } from "../constants/swaps.constants";

export interface SwapCreateRequest {
  /** Quote id. The backend reads amounts, rate and fees from the frozen quote — nothing else is sent. */
  quote: string;
  /** INTERNAL users only — members are pinned server-side from the JWT. */
  organization_id?: string;
}

/** Post-swap state of one wallet. `balance` is an object, not a number: the runtime wallet has three. */
export interface SwapWallet {
  id: string;
  currency: string;
  status: "active" | "frozen" | "closed";
  balance: { available: string; pending: string; blocked: string };
  updated_at: string;
}

/**
 * All money is a decimal string (8 dp) — format with Intl, never `Number` arithmetic.
 *
 * Note there is no `fees.percentage_fee`: the legacy PHP response used that one name for a _fraction_ here
 * and an _amount_ in the quote response. It is split into `percentage_rate` and `percentage_amount`.
 */
export interface NativeSwap {
  id: string;
  status: "CREATED" | "COMPLETED" | "FAILED";
  quote_id: string;
  summary: {
    origin_currency: string;
    destination_currency: string;
    /** The quote's frozen mid rate. */
    rate_applied: string;
    /** Amount_received / total_debited — the all-in price, which is what a UI should show. */
    effective_rate: string;
    /** Gross debit from the origin wallet, fee included. */
    total_debited: string;
    /** Total_debited − fees.total: the amount that actually converted. */
    net_converted: string;
    amount_received: string;
    fees: {
      currency: string;
      percentage_rate: string;
      percentage_amount: string;
      fixed_amount: string;
      total: string;
    };
  };
  wallets: { origin: SwapWallet; destination: SwapWallet };
  created_at: string;
}

export interface SwapResponse {
  swap: NativeSwap;
}

/**
 * A row of `GET /internal/payments/swaps`, which returns the **persisted `SwapEntity`** — not the `{ swap: …
 * }` summary shape of the create/detail endpoints. No relation is joined, so only the `*_id` columns come
 * back. Every money column is a decimal string at 8 dp.
 */
export interface SwapListEntity {
  id: string;
  organization_id: string;
  quote_id: string;
  origin_currency: string;
  destination_currency: string;
  origin_wallet_id: string;
  destination_wallet_id: string;
  /** The quote's frozen mid rate. */
  rate_applied: string;
  /** Gross debit from the origin wallet, fee included. */
  total_debited: string;
  /** Credited to the destination wallet. */
  amount_received: string;
  /** Always denominated in `origin_currency`. */
  fee_total: string;
  status: NativeSwap["status"];
  created_at: string;
  updated_at: string;
}

/** Query params for the swaps list — mirrors coloca-nest `SwapsQueryDto` (all AND-combined). */
export interface ListSwapsParams {
  /**
   * Tenancy: members are pinned server-side; INTERNAL staff may omit it to list cross-org, or pass one client
   * organization to narrow the list.
   */
  organizationId?: string | null;
  status?: NativeSwap["status"];
  origin_currency?: string;
  destination_currency?: string;
  /** `created_at >= from` (ISO 8601). */
  from?: string;
  /** `created_at <= to` (ISO 8601). */
  to?: string;
  /** Page size — backend default 10, max 200. */
  limit?: number;
  /** Offset — backend default 0. */
  skip?: number;
}

/** Paginated shape of the runtime list endpoints (`PaginatedResponse<T>` in coloca-nest). */
export interface PaginatedRuntimeSwaps {
  data: SwapListEntity[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

/** One key per swap attempt; retries of the SAME attempt must reuse it. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export class SwapService {
  /**
   * Redeem a quote and move funds between the organization's own wallets, in one backend transaction.
   *
   * The `Idempotency-Key` header makes a network retry safe: the backend replays the original swap instead of
   * moving money twice, and — critically — does NOT answer `quote_already_used`, which is what a second
   * attempt with a _different_ key would get.
   */
  static async createSwap(
    data: SwapCreateRequest,
    idempotencyKey: string,
  ): Promise<AxiosResponse<SwapResponse>> {
    return HttpApiService.post<SwapResponse, SwapCreateRequest>(SWAPS_API_ENDPOINTS.SWAPS, data, {
      headers: { "Idempotency-Key": idempotencyKey },
    });
  }

  /**
   * Fetch one swap.
   *
   * `organizationId` is not optional in practice for INTERNAL staff: `InternalSwapsService.findOne` resolves
   * the organization through `requireOrg` and answers **400 `organization_required`** when a caller with no
   * organization of its own omits it. Members are pinned server-side and must not send it. Another org's swap
   * is a 404, never a 403.
   */
  static async getSwap(id: string, organizationId?: string | null): Promise<AxiosResponse<SwapResponse>> {
    const suffix = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
    return HttpApiService.get<SwapResponse>(`${SWAPS_API_ENDPOINTS.byId(id)}${suffix}`);
  }

  /**
   * List swaps, newest first (`created_at DESC`). Same tenancy rule as the payins/payouts lists: org-scoped
   * callers always pass `organizationId`; only INTERNAL staff views may omit it to list cross-org.
   */
  static async listSwaps(params: ListSwapsParams): Promise<AxiosResponse<PaginatedRuntimeSwaps>> {
    const query = new URLSearchParams();
    if (params.organizationId) query.set("organization_id", params.organizationId);
    if (params.status) query.set("status", params.status);
    if (params.origin_currency) query.set("origin_currency", params.origin_currency);
    if (params.destination_currency) query.set("destination_currency", params.destination_currency);
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.skip != null) query.set("skip", String(params.skip));
    const suffix = query.size > 0 ? `?${query}` : "";
    return HttpApiService.get<PaginatedRuntimeSwaps>(`${SWAPS_API_ENDPOINTS.SWAPS}${suffix}`);
  }
}
